/**
 * OperatorsTab — Quiénes operan las cajas del POS (PLAN_POS_IMPLEMENTACION F5).
 *
 * Dos altas distintas, a propósito:
 * - «Nuevo cajero»: una persona que solo usa el terminal. Licencia POS, sin
 *   contraseña ni invitación; no entra a la web (D9).
 * - «Habilitar usuario»: alguien que ya usa Clobi (un encargado, un
 *   administrador) y además opera una caja. Su licencia no cambia.
 */
import { useMemo, useState } from 'react'
import { Plus, Pencil, UserCheck, KeyRound } from 'lucide-react'
import { usePermission } from '@/hooks/usePermission'
import { useToast, ToastContainer } from '@/components/Toast'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { Button } from '@/components/Button'
import { DataTable } from '@/components/ds/DataTable'
import { Modal } from '@/components/ds/Modal'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { CheckboxField } from '@/components/ds/CheckboxField'
import { StatusBadge } from '@/components/ds/StatusBadge'
import { EmptyState } from '@/components/EmptyState'
import { extractErrorMessage } from '@/lib/errorUtils'
import { useTenantUsers } from '@/modules/users/hooks/useUsers'
import {
  usePosOperators, usePosStores, useCreatePosOperator, useUpdatePosOperator,
  type PosOperator, type PosAssignableRole,
} from '../hooks/usePos'

const PIN_HINT = 'Solo números, de 4 a 8 dígitos (6 o más si autoriza en la caja). Se cambia en la caja la primera vez.'

export default function OperatorsTab() {
  const { can } = usePermission()
  const toast = useToast()
  const { data, isLoading } = usePosOperators()
  const [modal, setModal] = useState<'cajero' | 'usuario' | PosOperator | null>(null)
  const operators = data?.operators ?? []
  const licencias = data?.pos_licenses
  const sinCupo = !!licencias && licencias.in_use >= licencias.contracted

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 text-sm text-content-muted">
          <span>{operators.length} {operators.length === 1 ? 'operador' : 'operadores'}</span>
          {licencias && (
            <StatusBadge
              status={sinCupo ? 'warning' : 'active'}
              tone={sinCupo ? 'warning' : 'neutral'}
              label={`Licencias POS: ${licencias.in_use} de ${licencias.contracted}`}
            />
          )}
        </div>
        {can('pos.operators', 'create') && (
          <div className="flex gap-2">
            {can('users', 'view') && (
              <Button variant="secondary" onClick={() => setModal('usuario')}>
                <UserCheck size={14} /> Habilitar usuario
              </Button>
            )}
            <Button onClick={() => setModal('cajero')} disabled={sinCupo}
              title={sinCupo ? 'Todas las licencias POS están en uso.' : undefined}>
              <Plus size={14} /> Nuevo cajero
            </Button>
          </div>
        )}
      </div>

      <DataTable<PosOperator>
        rows={operators}
        rowKey={(o) => o.user_id}
        loading={isLoading}
        empty={<EmptyState title="Nadie opera las cajas todavía."
          hint="Un cajero nuevo usa solo el terminal, con su PIN. Un usuario que ya usa Clobi también se puede habilitar en la caja." size="sm" />}
        columns={[
          {
            key: 'name', header: 'Nombre',
            render: (o) => (
              <div>
                <div className="text-content">{o.name}</div>
                <div className="text-xs text-content-muted">{o.email}</div>
              </div>
            ),
          },
          {
            key: 'license', header: 'Licencia',
            render: (o) => o.license_type === 'pos'
              ? <StatusBadge status="pos" tone="info" label="Cajero (POS)" />
              : <StatusBadge status="full" tone="neutral" label="Completa" />,
          },
          { key: 'roles', header: 'Rol', render: (o) => o.roles.map((r) => r.name).join(', ') || '—' },
          { key: 'stores', header: 'Locales', render: (o) => o.stores.map((s) => s.code).join(', ') || '—' },
          {
            key: 'pin', header: 'PIN',
            render: (o) => !o.has_pin
              ? <StatusBadge status="none" tone="neutral" label="Sin PIN" />
              : o.pin_must_change
                ? <StatusBadge status="pending" tone="warning" label="Cambiar en la caja" />
                : <StatusBadge status="ok" tone="success" label="Vigente" />,
          },
          {
            key: 'status', header: 'Estado',
            render: (o) => (
              <StatusBadge status={o.is_active ? 'active' : 'inactive'} label={o.is_active ? 'Activo' : 'De baja'}
                tone={o.is_active ? 'success' : 'neutral'} />
            ),
          },
          {
            key: 'actions', header: '',
            render: (o) => can('pos.operators', 'edit') && (
              <button onClick={() => setModal(o)} title="Editar" aria-label={`Editar ${o.name}`}
                className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-content-muted">
                <Pencil size={14} />
              </button>
            ),
          },
        ]}
      />

      {modal === 'cajero' && (
        <NewCashierModal roles={data?.assignable_roles ?? []} onClose={() => setModal(null)}
          onSaved={() => { toast.success('Cajero creado'); setModal(null) }} />
      )}
      {modal === 'usuario' && (
        <EnableUserModal operators={operators} onClose={() => setModal(null)}
          onSaved={() => { toast.success('Usuario habilitado en el terminal'); setModal(null) }} />
      )}
      {modal && typeof modal === 'object' && (
        <EditOperatorModal operator={modal} roles={data?.assignable_roles ?? []} onClose={() => setModal(null)}
          onSaved={(msg) => { toast.success(msg); setModal(null) }} />
      )}
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

// ─── Piezas de los formularios ───────────────────────────────────────────────

function StoresPicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const { data: stores = [] } = usePosStores()
  const activos = stores.filter((s) => s.is_active)
  return (
    <fieldset>
      <legend className="cl-label mb-1.5">Locales <span className="text-danger-solid">*</span></legend>
      {activos.length === 0
        ? <p className="text-sm text-content-muted">No hay locales activos. Se crean en Locales.</p>
        : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {activos.map((s) => (
              <CheckboxField key={s.id} label={`${s.code} · ${s.name}`}
                checked={value.includes(s.id)}
                onChange={() => onChange(value.includes(s.id) ? value.filter((x) => x !== s.id) : [...value, s.id])} />
            ))}
          </div>
        )}
    </fieldset>
  )
}

function PinField({ value, onChange, label = 'PIN inicial', required = true }: {
  value: string; onChange: (v: string) => void; label?: string; required?: boolean
}) {
  return (
    <TextField dense label={label} required={required} value={value} hint={PIN_HINT}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 8))}
      inputProps={{ type: 'password', inputMode: 'numeric', autoComplete: 'new-password' }} />
  )
}

function useGuardar<T>(fn: (v: T, opts: { onSuccess: () => void; onError: (e: unknown) => void }) => void, fallback: string) {
  const [error, setError] = useState<string | null>(null)
  const guardar = (v: T, onSaved: () => void) => {
    setError(null)
    fn(v, { onSuccess: onSaved, onError: (e) => setError(extractErrorMessage(e, fallback)) })
  }
  return { error, guardar }
}

// ─── Nuevo cajero ────────────────────────────────────────────────────────────

function NewCashierModal({ roles, onClose, onSaved }: {
  roles: PosAssignableRole[]; onClose: () => void; onSaved: () => void
}) {
  const { mutate, isPending } = useCreatePosOperator()
  const { error, guardar } = useGuardar(mutate, 'No se pudo crear el cajero.')
  const porDefecto = roles.find((r) => r.role_type === 'cashier')?.id ?? ''
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', role: porDefecto, pin: '' })
  const [stores, setStores] = useState<string[]>([])
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value })
  const listo = form.first_name.trim() && form.email.trim() && form.role && stores.length && form.pin.length >= 4

  return (
    <Modal open onClose={onClose} title="Nuevo cajero" size="lg" dismissOnScrimClick={false}
      subtitle="Usa solo el terminal, con su PIN. No entra a la web y ocupa una licencia POS."
      actions={<>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button disabled={!listo} isPending={isPending} onClick={() => guardar({
          email: form.email.trim(), first_name: form.first_name.trim(), last_name: form.last_name.trim(),
          role_ids: [form.role], store_ids: stores, pin: form.pin,
        }, onSaved)}>Crear cajero</Button>
      </>}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField dense label="Nombre" required value={form.first_name} onChange={set('first_name')} />
          <TextField dense label="Apellido" value={form.last_name} onChange={set('last_name')} />
        </div>
        <TextField dense label="Correo" required value={form.email} onChange={set('email')}
          hint="Identifica a la persona en Clobi. No recibe invitación ni contraseña."
          inputProps={{ type: 'email', autoComplete: 'off' }} />
        <SelectField dense label="Rol" required value={form.role} onChange={set('role')}
          placeholder="Seleccionar el rol"
          hint="Define lo que puede hacer en la caja (Usuarios → Roles → En el terminal del POS)."
          options={roles.map((r) => ({ value: r.id, label: r.is_web ? `${r.name} (también da permisos en la web)` : r.name }))} />
        <StoresPicker value={stores} onChange={setStores} />
        <PinField value={form.pin} onChange={(pin) => setForm({ ...form, pin })} />
        {error && <p className="text-sm text-danger-content">{error}</p>}
      </div>
    </Modal>
  )
}

// ─── Habilitar a un usuario de la empresa ────────────────────────────────────

function EnableUserModal({ operators, onClose, onSaved }: {
  operators: PosOperator[]; onClose: () => void; onSaved: () => void
}) {
  const { data } = useTenantUsers()
  const { mutate, isPending } = useCreatePosOperator()
  const { error, guardar } = useGuardar(mutate, 'No se pudo habilitar al usuario.')
  const [userId, setUserId] = useState('')
  const [stores, setStores] = useState<string[]>([])
  const [pin, setPin] = useState('')
  const yaOperan = useMemo(() => new Set(operators.map((o) => o.user_id)), [operators])
  const candidatos = (data?.users ?? []).filter(
    (u) => u.is_active && u.membership_status === 'accepted' && !yaOperan.has(u.id),
  )

  return (
    <Modal open onClose={onClose} title="Habilitar usuario en el terminal" size="lg" dismissOnScrimClick={false}
      subtitle="Para quien ya usa Clobi y además opera una caja. Su licencia no cambia y no ocupa una licencia POS."
      actions={<>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button disabled={!userId || !stores.length || pin.length < 4} isPending={isPending}
          onClick={() => guardar({ user_id: userId, store_ids: stores, pin }, onSaved)}>Habilitar</Button>
      </>}>
      <div className="space-y-4">
        <SelectField dense label="Usuario" required value={userId} onChange={(e) => setUserId(e.target.value)}
          placeholder="Seleccionar el usuario"
          hint="Lo que puede hacer en la caja sale de sus roles."
          options={candidatos.map((u) => ({ value: u.id, label: `${u.full_name || u.email} · ${u.email}` }))} />
        <StoresPicker value={stores} onChange={setStores} />
        <PinField value={pin} onChange={setPin} />
        {error && <p className="text-sm text-danger-content">{error}</p>}
      </div>
    </Modal>
  )
}

// ─── Editar ──────────────────────────────────────────────────────────────────

function EditOperatorModal({ operator, roles, onClose, onSaved }: {
  operator: PosOperator; roles: PosAssignableRole[]; onClose: () => void; onSaved: (msg: string) => void
}) {
  const { mutate, isPending } = useUpdatePosOperator()
  const { error, guardar } = useGuardar(mutate, 'No se pudieron guardar los cambios.')
  const { confirm, modalProps } = useConfirmModal()
  const esCajero = operator.license_type === 'pos'
  const [role, setRole] = useState(operator.roles[0]?.id ?? '')
  const [stores, setStores] = useState(operator.stores.map((s) => s.id))
  const [pin, setPin] = useState('')
  const [nuevoPin, setNuevoPin] = useState(false)

  const darDeBaja = () => confirm({
    message: esCajero
      ? `¿Dar de baja a ${operator.name}? Deja de poder operar las cajas en su siguiente sincronización y libera su licencia POS.`
      : `¿Quitar a ${operator.name} del terminal? Sigue usando la web como siempre; solo deja de operar las cajas.`,
    confirmLabel: esCajero ? 'Dar de baja' : 'Quitar del terminal',
    onConfirm: () => guardar(
      { userId: operator.user_id, is_active: false },
      () => onSaved(esCajero ? `${operator.name} dado de baja` : `${operator.name} ya no opera el terminal`),
    ),
  })

  const guardarCambios = () => guardar({
    userId: operator.user_id,
    store_ids: stores,
    ...(esCajero && role ? { role_ids: [role] } : {}),
    ...(nuevoPin && pin ? { pin } : {}),
    ...(!operator.is_active ? { is_active: true } : {}),
  }, () => onSaved('Cambios guardados'))

  return (
    <Modal open onClose={onClose} title={operator.name} subtitle={operator.email} size="lg" dismissOnScrimClick={false}
      actions={<>
        {operator.is_active && (
          <Button variant="secondary" onClick={darDeBaja} className="mr-auto">
            {esCajero ? 'Dar de baja' : 'Quitar del terminal'}
          </Button>
        )}
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button disabled={!stores.length || (nuevoPin && pin.length < 4)} isPending={isPending} onClick={guardarCambios}>
          {operator.is_active ? 'Guardar' : 'Reactivar y guardar'}
        </Button>
      </>}>
      <div className="space-y-4">
        {esCajero ? (
          <SelectField dense label="Rol" required value={role} onChange={(e) => setRole(e.target.value)}
            options={roles.map((r) => ({ value: r.id, label: r.name }))} />
        ) : (
          <p className="text-sm text-content-muted">
            Rol: {operator.roles.map((r) => r.name).join(', ')}. Los roles de un usuario con licencia completa se cambian en Usuarios.
          </p>
        )}
        <StoresPicker value={stores} onChange={setStores} />
        {nuevoPin ? (
          <PinField label="PIN nuevo" value={pin} onChange={setPin} />
        ) : (
          <Button variant="secondary" onClick={() => setNuevoPin(true)}>
            <KeyRound size={14} /> Asignar un PIN nuevo
          </Button>
        )}
        {error && <p className="text-sm text-danger-content">{error}</p>}
      </div>
      <ConfirmModal {...modalProps} />
    </Modal>
  )
}
